import React from 'react';
import { X } from 'lucide-react';

// 直接在组件中定义类型，避免依赖问题
type PlanVersion = {
  id: number;
  version_number: string;
  created_by_username: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  plans: PlanVersion[];
  selectedPlanVersion: number;
  lessonInfo?: {
    id: number;
    title: string;
    sort_order: number;
  };
  stageInfo?: {
    title: string;
  };
  wsConnectionStatus: 'not_connected' | 'connecting' | 'connected' | 'disconnected';
  prompts: Array<{ id: number; name: string; category: string }>;
  selectedPromptId: number | null;
  setSelectedPromptId: (id: number | null) => void;
  isGenerating: boolean;
  generatedSlides: string[];
  slideCount: number;
  setSlideCount: (count: number) => void;
  startIndex: number;
  setStartIndex: (index: number) => void;
  onStartGeneration: () => void;
  onStopGeneration: () => void;
  debugWebSocketConnection: () => void;
  // [NEW] 新增模式属性
  generateMode: 'slides' | 'guides';
};

export const BatchGenerateModal = ({
  isOpen,
  onClose,
  plans,
  selectedPlanVersion,
  lessonInfo,
  stageInfo,
  wsConnectionStatus,
  prompts,
  selectedPromptId,
  setSelectedPromptId,
  isGenerating,
  generatedSlides,
  slideCount,
  setSlideCount,
  startIndex,
  setStartIndex,
  onStartGeneration,
  onStopGeneration,
  debugWebSocketConnection,
  generateMode = 'slides' // 默认值为 slides
}: Props) => {
  // ✅ Hooks 必须在任何 return 语句之前调用
  // 用于自动滚动的ref
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  // 当消息列表更新时，自动滚动到底部
  React.useEffect(() => {
    // 只有在弹窗打开且有新消息时才滚动
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [generatedSlides, isOpen]);

  // 根据生成模式过滤提示词
  const filteredPrompts = React.useMemo(() => {
    const category = generateMode === 'slides' ? 'gen_slide' : 'gen_plan_side';
    return prompts.filter(prompt => prompt.category === category);
  }, [prompts, generateMode]);

  // 设置默认选中的提示词
  React.useEffect(() => {
    if (filteredPrompts.length > 0) {
      // 检查当前选中的提示词是否在过滤后的列表中
      const isSelectedPromptValid = filteredPrompts.some(prompt => prompt.id === selectedPromptId);
      if (!isSelectedPromptValid) {
        // 如果当前选中的提示词无效，默认选中第一个
        setSelectedPromptId(filteredPrompts[0].id);
      }
    } else {
      // 如果没有匹配的提示词，清空选中状态
      setSelectedPromptId(null);
    }
  }, [filteredPrompts, selectedPromptId, setSelectedPromptId]);

  // ✅ 条件判断放在 Hooks 之后
  if (!isOpen) return null;

  // 辅助变量：判断当前是否为生成课件模式
  const isSlidesMode = generateMode === 'slides';
  const title = isSlidesMode ? '批量生成初版课件' : '批量生成每页教案';
  const actionDescription = isSlidesMode ? '批量生成初版slide' : '批量生成每页教案';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[80vw] h-[80vh] overflow-hidden flex flex-col">
        {/* 模态框头部 */}
        <div className="flex items-center justify-between mb-4 border-b pb-4">
          <div>
            <h3 className="text-xl font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">
              即将为 {stageInfo?.title} 的第 {lessonInfo?.sort_order} 课的第 {plans.find(p => p.id === selectedPlanVersion)?.version_number || selectedPlanVersion} 版教案{actionDescription}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* WebSocket连接状态 */}
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${wsConnectionStatus === 'connected' ? 'bg-green-500' : wsConnectionStatus === 'connecting' ? 'bg-yellow-500' : wsConnectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-gray-400'}`}></div>
              <span className={`text-sm font-medium ${wsConnectionStatus === 'connected' ? 'text-green-600' : wsConnectionStatus === 'connecting' ? 'text-yellow-600' : wsConnectionStatus === 'disconnected' ? 'text-red-600' : 'text-gray-500'}`}>
                {wsConnectionStatus === 'connected' ? '已连接' : 
                 wsConnectionStatus === 'connecting' ? '连接中' : 
                 wsConnectionStatus === 'disconnected' ? '已断开' : '未连接'}
              </span>
            </div>
            
            {/* 调试按钮 */}
            <button
              onClick={debugWebSocketConnection}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
              title="生成调试信息"
            >
              调试
            </button>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* 模态框内容 */}
        <div className="flex-1 overflow-auto p-2">
          <div className="space-y-6">
            {/* 提示词选择和幻灯片数量设置 */}
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex flex-col md:flex-row gap-4">
                
                {/* 系统提示词选择 */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    系统提示词选择
                  </label>
                  <select
                    value={selectedPromptId || ''}
                    onChange={(e) => setSelectedPromptId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {filteredPrompts.map(prompt => (
                      <option key={prompt.id} value={prompt.id}>
                        {prompt.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* 幻灯片数量和开始位置设置 */}
                <div className="flex flex-col md:flex-row gap-4">
                  {/* 生成幻灯片数量 - [NEW] 仅在 slides 模式下显示 */}
                  {isSlidesMode && (
                    <div className="w-full md:w-32">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        生成幻灯片数量
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={slideCount}
                        onChange={(e) => setSlideCount(parseInt(e.target.value) || 22)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="22"
                      />
                    </div>
                  )}
                  
                  {/* 开始位置设置 */}
                  <div className="w-full md:w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      开始位置
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={startIndex}
                      onChange={(e) => setStartIndex(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 生成状态和结果 */}
            <div className="bg-white border border-gray-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">生成状态</h4>
              <div className="space-y-2">
                {isGenerating ? (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>正在生成{isSlidesMode ? '幻灯片' : '教案'}...</span>
                  </div>
                ) : (
                  <span className="text-gray-500">准备就绪</span>
                )}
                
                {/* 生成结果 */}
                {generatedSlides.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">生成结果</h5>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {generatedSlides.map((slide, index) => (
                        <div key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md">
                          {slide}
                        </div>
                      ))}
                      {/* 用于自动滚动的元素 */}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 模态框底部 */}
        <div className="flex justify-end gap-3 mt-4 border-t pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm"
            disabled={isGenerating}
          >
            取消
          </button>
          <button
            onClick={isGenerating ? onStopGeneration : onStartGeneration}
            className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${isGenerating ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}
          >
            {isGenerating ? 'Stop' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
};
